/** Avida Coach — today's focus panel for My Day.
 * Shows a ranked stack of next-best-actions in brand voice. */
import { useEffect, useState } from "react";
import api from "@/api/client";
import { toast } from "sonner";
import { Sparkles, Calendar, Ban, AlertTriangle, Clock, ListChecks, AtSign, TrendingUp, ArrowRight, Check, Clock4 } from "lucide-react";
import AvidaMark from "@/components/AvidaMark";

const ICON_MAP = {
  calendar: Calendar,
  ban: Ban,
  alert: AlertTriangle,
  clock: Clock,
  tasks: ListChecks,
  at: AtSign,
  trending: TrendingUp,
};

const PRIORITY_STYLE = {
  urgent: { tag: "bg-[#b91c1c] text-white", label: "URGENT" },
  high:   { tag: "bg-[#E65100] text-white", label: "HIGH" },
  medium: { tag: "bg-[#FFCB05] text-[#15233B]", label: "MEDIUM" },
  info:   { tag: "bg-[#1E73C9] text-white", label: "FYI" },
};

export default function CoachCard({ onOpenCard, onNavigate }) {
  const [data, setData] = useState(null);
  const reload = () => api.get("/coach/suggestions").then(({ data }) => setData(data));
  useEffect(() => { reload(); }, []);

  const dismiss = async (e, key, hours, label) => {
    e.stopPropagation();
    // optimistic
    setData((d) => d ? { ...d, suggestions: d.suggestions.filter((s) => s.key !== key) } : d);
    try {
      await api.post("/coach/dismiss", { key, hours });
      toast.success(label);
    } catch {
      toast.error("Could not snooze — refreshing");
      reload();
    }
  };

  if (!data) return null;
  const total = data.counts.urgent + data.counts.high + data.counts.medium + data.counts.info;

  return (
    <section
      data-testid="coach-card"
      className="bg-[#15233B] text-white rounded-xl shadow-lg overflow-hidden relative"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_15%_25%,#FFCB05_0,transparent_45%),radial-gradient(circle_at_85%_75%,#1E73C9_0,transparent_55%)]"
      />
      <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 relative">
        <div className="h-9 w-9 rounded-lg bg-[#FFCB05] grid place-items-center shrink-0 shadow-sm">
          <Sparkles className="h-5 w-5 text-[#15233B]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FFCB05]">
            Avida Coach · Today's focus
          </div>
          <div className="text-base font-extrabold tracking-tight">
            {data.user.name}, here's what needs you most.
          </div>
        </div>
        <AvidaMark size="xs" withWordmark={false} className="opacity-90 hidden sm:flex" />
      </div>

      {total === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-white/70 relative">
          You're caught up. Nothing urgent on your plate — go take the win. 🌟
        </div>
      ) : (
        <ul className="divide-y divide-white/10 relative">
          {data.suggestions.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || Sparkles;
            const ps = PRIORITY_STYLE[s.priority] || PRIORITY_STYLE.medium;
            const onClick = () => {
              if (s.card_id) onOpenCard?.(s.card_id);
              else if (s.route) onNavigate?.(s.route);
            };
            return (
              <li key={s.key || i} className="group">
                <div className="w-full flex items-center gap-3 px-5 py-3">
                  <button
                    onClick={onClick}
                    data-testid={`coach-suggestion-${i}`}
                    className="flex-1 flex items-center gap-3 text-left hover:opacity-90"
                  >
                    <Icon className="h-4 w-4 text-[#FFCB05] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-extrabold tracking-[0.15em] px-1.5 py-0.5 rounded ${ps.tag}`}>
                          {ps.label}
                        </span>
                        <span className="font-bold text-white truncate">{s.title}</span>
                      </div>
                      <div className="text-xs text-white/65 mt-0.5 truncate">{s.detail}</div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-white/40 shrink-0" />
                  </button>
                  <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => dismiss(e, s.key, 24 * 7, "Marked done for the week")}
                      data-testid={`coach-done-${i}`}
                      title="Mark done (hide for 7 days)"
                      className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-[#A5D6A7]"
                    >
                      <Check className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={(e) => dismiss(e, s.key, 24, "Snoozed until tomorrow")}
                      data-testid={`coach-snooze-${i}`}
                      title="Snooze 24h"
                      className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-[#FFCB05]"
                    >
                      <Clock4 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="h-[2px] bg-gradient-to-r from-transparent via-[#FFCB05] to-transparent" aria-hidden />
    </section>
  );
}
